import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Sample, Test, Assay, Order, ListOptions, QueryFilter, SearchColumn } from '@mariana/labos-client';
import type { EntityType, PaginationInfo } from '../types';
import { limsClient } from '../api/limsClient';
import { resolveSourceColumns } from '../utils/resolveColumns';
import type { ResolvedEntityMap } from '../utils/resolveColumns';
import type { ColumnDef } from '../config/viewsConfig';
import type { UseEntityListResult } from './useEntityList';

type AnyEntity = Sample | Test | Assay | Order;

interface UseLocalEntityListOptions {
  entityType: EntityType;
  columns: ColumnDef[];
  initialFilters?: QueryFilter[];
  extraFilters?: QueryFilter[];
  defaultSort?: { by: string; order: 'asc' | 'desc' };
  search?: string;
  initialLimit?: number;
}

function deriveSearchColumns(columns: ColumnDef[]): SearchColumn[] {
  const cols: SearchColumn[] = [];
  for (const col of columns) {
    if (col.extract) continue;
    if (col.render === 'date') continue;
    if (col.key === 'id') continue;
    cols.push({ attribute: col.key });
  }
  return cols;
}

/**
 * URL-free variant of useEntityList. Manages pagination, sort, and fetch state
 * entirely in local React state so multiple instances can coexist on the same page.
 */
export function useLocalEntityList({
  entityType,
  columns,
  initialFilters = [],
  extraFilters = [],
  defaultSort,
  search,
  initialLimit = 25,
}: UseLocalEntityListOptions): UseEntityListResult {
  const searchCols = useMemo(() => deriveSearchColumns(columns), [columns]);
  const defaultSortBy = defaultSort?.by ?? 'id';
  const defaultSortOrder = defaultSort?.order ?? 'desc';

  const [entities, setEntities] = useState<AnyEntity[]>([]);
  const [resolvedEntities, setResolvedEntities] = useState<ResolvedEntityMap>(new Map());
  const [pagination, setPagination] = useState<PaginationInfo>({ limit: initialLimit, offset: 0, count: 0 });
  const [filters, setFilters] = useState<QueryFilter[]>(initialFilters);
  const [sortBy, setSortBy] = useState<string | undefined>(defaultSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(defaultSortOrder);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchTrigger = useMemo(() => JSON.stringify({
    filters, sortBy, sortOrder,
    limit: pagination.limit, offset: pagination.offset,
    extraFilters, search, refreshKey,
  }), [filters, sortBy, sortOrder, pagination.limit, pagination.offset, extraFilters, search, refreshKey]);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const mergedFilters = [...filters, ...extraFilters];
        const options: ListOptions = {
          filters: mergedFilters,
          limit: pagination.limit,
          offset: pagination.offset,
          ...(sortBy && { sort_by: sortBy, sort_order: sortOrder }),
          exclude_attributes: ['raw_worksheet_data'],
          ...(search && { search, search_columns: searchCols }),
        };

        const response = await limsClient.listEntitiesByType(entityType, options);
        if (controller.signal.aborted) return;

        let resolved: ResolvedEntityMap = new Map();
        try {
          resolved = await resolveSourceColumns(response.data, columns, entityType);
        } catch (resolveErr) {
          console.warn('Failed to resolve cross-entity columns:', resolveErr);
        }
        if (controller.signal.aborted) return;

        setEntities(response.data);
        setResolvedEntities(resolved);
        setPagination(prev => ({
          ...prev,
          count: response.pagination.count,
        }));
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load entities');
        console.error('Failed to load entities:', err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, fetchTrigger]);

  const handleFiltersChange = useCallback((newFilters: QueryFilter[]) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, offset: 0 }));
  }, []);

  const handlePageChange = useCallback((newOffset: number) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  }, []);

  const handleLimitChange = useCallback((newLimit: number) => {
    setPagination(prev => ({ ...prev, limit: newLimit, offset: 0 }));
  }, []);

  const handleSortChange = useCallback((col: string) => {
    setSortBy(prev => {
      const newOrder = prev === col && sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newOrder);
      return col;
    });
    setPagination(prev => ({ ...prev, offset: 0 }));
  }, [sortOrder]);

  const refreshEntities = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const fetchAllEntities = useCallback(async (
    onProgress: (fetched: number, total: number) => void,
  ): Promise<AnyEntity[]> => {
    const mergedFilters = [...filters, ...extraFilters];
    const total = pagination.count;
    const allEntities: AnyEntity[] = [];
    let offset = 0;
    const batchSize = 500;

    while (offset < total) {
      const options: ListOptions = {
        filters: mergedFilters,
        limit: batchSize,
        offset,
        ...(sortBy && { sort_by: sortBy, sort_order: sortOrder }),
        exclude_attributes: ['raw_worksheet_data'],
        ...(search && { search, search_columns: searchCols }),
      };
      const response = await limsClient.listEntitiesByType(entityType, options);
      allEntities.push(...response.data);
      offset += batchSize;
      onProgress(allEntities.length, total);
    }

    return allEntities;
  }, [entityType, filters, extraFilters, pagination.count, sortBy, sortOrder, search, searchCols]);

  return {
    entities,
    resolvedEntities,
    pagination,
    filters,
    sortBy,
    sortOrder,
    loading,
    error,
    handleFiltersChange,
    handlePageChange,
    handleLimitChange,
    handleSortChange,
    refreshEntities,
    fetchAllEntities,
  };
}
